import { rootId } from '@griply/types';
import { useCallback, useEffect, useRef } from 'react';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { ID } from 'shared/types/id';

import { TreeItem } from './types';

export type Options = {
  onCollapse?: (ids: ID[]) => void;
  onEscape?: () => void;
};

export const useKeyboardShortcuts = (
  items: Record<ID, TreeItem>,
  { onCollapse, onEscape }: Options,
) => {
  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const expandAll = useCallback(() => onCollapse?.([]), [onCollapse]);

  const collapseAll = useCallback(() => {
    // Only foldable sections may be collapsed — never leaf rows (tasks /
    // new-task rows), which would bloat collapsedSections with every task id.
    const sectionIds = Object.values(itemsRef.current)
      .filter((item) => item.id !== rootId && !!item.children?.length)
      .map((item) => item.id);
    onCollapse?.(sectionIds);
  }, [onCollapse]);

  const handleEscape = useCallback(() => onEscape?.(), [onEscape]);

  useKeysDown(
    [
      ['ControlLeft', 'ShiftLeft', 'KeyE'],
      ['ControlRight', 'ShiftLeft', 'KeyE'],
      ['ControlLeft', 'ShiftRight', 'KeyE'],
      ['ControlRight', 'ShiftRight', 'KeyE'],
    ],
    expandAll,
  );

  useKeysDown(
    [
      ['ControlLeft', 'ShiftLeft', 'KeyC'],
      ['ControlLeft', 'ShiftRight', 'KeyC'],
      ['ControlRight', 'ShiftLeft', 'KeyC'],
      ['ControlRight', 'ShiftRight', 'KeyC'],
    ],
    collapseAll,
  );

  useKeysDown(['Escape'], handleEscape);
};
